/**
 * Bridge global para sistema de confirmações
 * 
 * Permite que código não-React (como requestTracker.ts) use o
 * sistema de confirmação centralizado através de uma função global.
 * 
 * Esta função deve ser inicializada pelo ConfirmationProvider.
 */

import type { ConfirmationOptions } from '../../components/feedback/ConfirmationProvider';
import { logger } from './terminalLogger';

/**
 * Função global de confirmação
 * Inicializada pelo ConfirmationProvider no mount
 */
let globalConfirm: ((options: ConfirmationOptions) => Promise<boolean>) | null = null;

/**
 * Define a função global de confirmação
 * Deve ser chamada pelo ConfirmationProvider
 */
export function setGlobalConfirm(confirmFn: (options: ConfirmationOptions) => Promise<boolean>) {
  globalConfirm = confirmFn;
}

/**
 * Limpa a função global de confirmação
 * Deve ser chamada pelo ConfirmationProvider no unmount
 */
export function clearGlobalConfirm() {
  globalConfirm = null;
}

/**
 * Solicita confirmação usando o sistema centralizado
 * Fallback para window.confirm se sistema não estiver disponível
 */
export async function requestConfirmation(options: ConfirmationOptions): Promise<boolean> {
  if (globalConfirm) {
    try {
      const result = await globalConfirm(options);
      return result;
    } catch (error) {
      logger.error('[GLOBAL_CONFIRM] Erro ao usar sistema centralizado:', error);
      // Fallback para window.confirm
    }
  }

  // Fallback: usar window.confirm nativo
  const message = options.title 
    ? `${options.title}\n\n${options.message}`
    : options.message;
  
  return window.confirm(message);
}

